import { resolveMediaIdentityTitle } from "@/components/OmniCine/media/resolveMediaTitle";
import { resolveDockContext } from "@/components/OmniCine/Docks/shared/mediaDock.helpers";
import type { VideoDockIdentity, VideoDockSelectorInput } from "@/components/OmniCine/Docks/video/videoDock.types";

function pickVideoMember(args: Pick<VideoDockIdentity, "binding" | "family" | "members">) {
  const preferredMemberId = args.binding?.videoMemberId ?? args.family?.preferredVideoMemberId;
  return preferredMemberId ? args.members.find((entry) => entry.id === preferredMemberId) ?? null : null;
}

function pickVideoPreviewUrl(
  input: VideoDockSelectorInput,
  resolved: Pick<VideoDockIdentity, "binding" | "family" | "members" | "metadata" | "focusedClipMediaUrl">,
): string | null {
  const member = pickVideoMember(resolved);
  return (
    input.externalPreview?.url ??
    input.focusMedia?.mediaUrl ??
    input.focusIntent?.mediaUrl ??
    resolved.metadata?.previewProxyUrl ??
    member?.mediaUrl ??
    resolved.metadata?.mediaUrl ??
    resolved.focusedClipMediaUrl ??
    null
  );
}

function buildLinkedAudioSummary(args: VideoDockIdentity): string | null {
  const audioMember = args.members.find((entry) => entry.kind === "audio" || entry.kind === "embeddedAudio");
  if (!audioMember) return null;
  return `Linked audio: ${audioMember.label}`;
}

export function selectVideoDockIdentity(input: VideoDockSelectorInput): VideoDockIdentity {
  const resolved = resolveDockContext(input);
  const member = pickVideoMember(resolved);
  const previewLabel =
    input.focusMedia?.label ??
    input.focusIntent?.clipLabel ??
    input.focusIntent?.label ??
    resolved.metadata?.sourceLabel ??
    resolved.focusedClipLabel ??
    member?.label ??
    input.externalPreview?.label ??
    input.selectedAsset?.label ??
    "Media Preview";
  const baseIdentity: VideoDockIdentity = {
    ...resolved,
    previewLabel,
    resolvedPreviewUrl: null,
    title: "",
    subtitle: "Video Focus",
    kindChip: "VIDEO",
    sourceChip: resolved.sourceChip,
    linkedAudioSummary: null,
  };
  const resolvedPreviewUrl = pickVideoPreviewUrl(input, resolved);
  const linkedAudioSummary = buildLinkedAudioSummary({ ...baseIdentity, resolvedPreviewUrl });

  return {
    ...baseIdentity,
    resolvedPreviewUrl,
    linkedAudioSummary,
    title: resolveMediaIdentityTitle({
      clipTitle: input.focusMedia?.label ?? input.focusIntent?.clipLabel ?? input.focusIntent?.label ?? resolved.focusedClipLabel,
      assetDisplayName: input.selectedAsset?.label ?? resolved.focusedClipLabel,
      importedLabel: previewLabel,
      mediaUrl: resolvedPreviewUrl,
      mediaId: input.focusMedia?.mediaId ?? resolved.focusedClipMediaId ?? input.selectedAsset?.id,
      kind: "video",
    }),
  };
}